import type { SignInMethod } from './api/session'
import { LinkButton } from './ui/Control'
import { Screen } from './ui/Screen'

/**
 * The way in, and the way it is refused.
 *
 * Both are the same screen on purpose: somebody who has just been turned away is still
 * looking at a sign-in page, and the refusal belongs on it rather than on a page of its own
 * that they would have to find their way back from.
 *
 * The buttons come from what the service offers. Adding a provider adds a button here
 * without a line of this file changing.
 */
export interface SignInProps {
  readonly methods: readonly SignInMethod[]
  /** The account that was turned away, when that is what just happened. */
  readonly refusedAccount: string | null
  /** Set when a sign-in broke off for a reason that is not a refusal. */
  readonly failed: boolean
  readonly contact: string
}

/** Long enough to recognise an account, short enough not to let a name take over the page. */
const NAME_LIMIT = 40

export function SignIn({ methods, refusedAccount, failed, contact }: SignInProps) {
  const shownAccount = refusedAccount === null ? null : refusedAccount.slice(0, NAME_LIMIT)

  return (
    <Screen>
      {shownAccount !== null && (
        <div className="notice notice--bad" role="alert">
          <h2 className="notice__title">
            The account <strong>{shownAccount}</strong> cannot be used here
          </h2>
          <p className="notice__text">
            MyLib is not open to everyone: only accounts that have been let in get a space. Yours is
            not one of them yet — nothing was created for it, and nothing was lost.
          </p>
          <p className="notice__text">
            If that is unexpected, ask {contact} to add the account name above. Signing in with a
            different account works right away.
          </p>
        </div>
      )}

      {failed && (
        <div className="notice notice--bad" role="alert">
          <h2 className="notice__title">That sign-in did not finish</h2>
          <p className="notice__text">
            Something went wrong between MyLib and the provider. Trying again usually settles it; if
            it does not, {contact} can look at the log.
          </p>
        </div>
      )}

      <p className="card__lead">Your documents, notes and timetable live in your own space.</p>
      <p className="card__detail">
        Sign in with an account you already have — MyLib keeps no password of its own. You stay
        signed in for weeks, on every device, until you sign out.
      </p>

      {methods.length === 0 ? (
        <p className="card__detail">
          No way to sign in has been set up on this installation yet. {contact} can add one.
        </p>
      ) : (
        <div className="controls">
          {methods.map((method) => (
            <LinkButton tone="primary" key={method.id} href={method.url}>
              Sign in with {method.name}
            </LinkButton>
          ))}
        </div>
      )}
    </Screen>
  )
}
